import { useEffect, useState } from "react";

import { useDebounce } from "./useDebounce";

interface UseEventTrackingProps<U> {
  createEvent: () => Promise<string>;
  patchEvent: (eventId: string, updatePayload: U) => Promise<void>;
  createEventDisabled?: boolean;
  debounceTime?: number;  // milliseconds
}

interface UseEventTrackingReturn<U> {
  onChange: (payload: U, immediateUpdate?: boolean) => void;
  eventId: string | null;
}

export const useEventTracking = <U>({
  createEvent,
  patchEvent,
  createEventDisabled,
  debounceTime
}: UseEventTrackingProps<U>): UseEventTrackingReturn<U> => {
  const [eventId, setEventId] = useState<string | null>(null);
  const [updatePayload, setUpdatePayload] = useState<U | null>(null);

  const onChange = (payload: U, immediateUpdate?: boolean) => {
    if (immediateUpdate && eventId) {
      patchEvent(eventId, payload);
    } else {
      setUpdatePayload(payload);
    }
  };

  const { debounced: debouncedValues } = useDebounce(
    updatePayload,
    debounceTime ?? 0,
    !!eventId
  );

  useEffect(() => {
    if (createEventDisabled) return;

    const _createEvent = async () => {
      const _eventId = await createEvent();

      setEventId(_eventId);
    };

    _createEvent();
  }, [createEventDisabled]);

  useEffect(() => {
    if (eventId && debouncedValues) {
      patchEvent(eventId, debouncedValues);
    }
  }, [debouncedValues]);

  return {
    onChange,
    eventId,
  };
};
